<script setup lang="ts">
import { Plus, Search } from 'lucide-vue-next';
import { computed, ref, watch } from 'vue';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@/components/ui/table';
import { useTenantStore } from '@/stores/tenant';
import { useUserStore } from '@/stores/user';

type MemberRole = 'admin' | 'member';
type MembersView = 'members' | 'invitations';

type WorkspaceMember = {
    id: string;
    name: string;
    email: string;
    role: MemberRole;
    joinedAt: string;
};

type WorkspaceInvitation = {
    id: string;
    email: string;
    role: MemberRole;
    invitedAt: string;
};

const emit = defineEmits<{
    (e: 'paid-seats-change', value: number): void;
}>();

const user = useUserStore();
const tenantStore = useTenantStore();

const membersView = ref<MembersView>('members');
const searchQuery = ref('');
const isInviteDialogOpen = ref(false);
const newMemberEmail = ref('');
const newMemberRole = ref<MemberRole>('member');
const inviteError = ref('');

const activeMembers = ref<WorkspaceMember[]>([
    {
        id: 'm-1',
        name: user.displayName || user.firstName || 'Current User',
        email: user.email || 'you@company.com',
        role: 'admin',
        joinedAt: 'Jun 13, 2025'
    },
    {
        id: 'm-2',
        name: 'Gabrielle Barbieri',
        email: 'gabriele@elqano.com',
        role: 'admin',
        joinedAt: 'Jun 13, 2025'
    },
    {
        id: 'm-3',
        name: 'Thomas Martin',
        email: 'thomas@elqano.com',
        role: 'member',
        joinedAt: 'Jun 15, 2025'
    }
]);

const pendingInvitations = ref<WorkspaceInvitation[]>([
    {
        id: 'i-1',
        email: 'new.joiner@company.com',
        role: 'member',
        invitedAt: 'Feb 20, 2026'
    }
]);

const canManageMembers = computed(() => tenantStore.tenantAdmin);

const filteredMembers = computed(() => {
    const query = searchQuery.value.trim().toLowerCase();
    if (!query) return activeMembers.value;
    return activeMembers.value.filter(
        (member) =>
            member.name.toLowerCase().includes(query) ||
            member.email.toLowerCase().includes(query)
    );
});

const filteredInvitations = computed(() => {
    const query = searchQuery.value.trim().toLowerCase();
    if (!query) return pendingInvitations.value;
    return pendingInvitations.value.filter((invite) =>
        invite.email.toLowerCase().includes(query)
    );
});

const paidSeats = computed(() => activeMembers.value.length);

watch(
    paidSeats,
    (value) => {
        emit('paid-seats-change', value);
    },
    { immediate: true }
);

const roleLabel = (role: MemberRole) => (role === 'admin' ? 'Admin' : 'Member');

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const formatDate = (date: Date) =>
    date.toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric'
    });

const getInitials = (value: string) => {
    const parts = value.trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

const openInviteDialog = () => {
    if (!canManageMembers.value) return;
    inviteError.value = '';
    newMemberEmail.value = '';
    newMemberRole.value = 'member';
    isInviteDialogOpen.value = true;
};

const addMemberInvitation = () => {
    if (!canManageMembers.value) return;

    const email = newMemberEmail.value.trim().toLowerCase();
    if (!emailRegex.test(email)) {
        inviteError.value = 'Please enter a valid email address.';
        return;
    }

    const alreadyMember = activeMembers.value.some(
        (member) => member.email.toLowerCase() === email
    );
    const alreadyInvited = pendingInvitations.value.some(
        (invite) => invite.email.toLowerCase() === email
    );

    if (alreadyMember || alreadyInvited) {
        inviteError.value = 'This user is already a member or already invited.';
        return;
    }

    pendingInvitations.value.unshift({
        id: `i-${Date.now()}`,
        email,
        role: newMemberRole.value,
        invitedAt: formatDate(new Date())
    });

    isInviteDialogOpen.value = false;
};
</script>

<template>
    <div class="mt-0 space-y-6">
        <div class="flex flex-col gap-3 border rounded-lg p-4 bg-muted/20">
            <div
                class="flex flex-col gap-2 md:flex-row md:items-center md:justify-between"
            >
                <div class="space-y-1">
                    <h2 class="text-xl font-semibold">Members</h2>
                    <p class="text-sm text-muted-foreground">
                        Manage members and invitations.
                    </p>
                </div>
                <Button
                    class="gap-2"
                    :disabled="!canManageMembers"
                    @click="openInviteDialog"
                >
                    <Plus class="h-4 w-4" />
                    Add member
                </Button>
            </div>
            <p class="text-sm text-muted-foreground">
                Billing:
                <span class="font-medium">27€/member/month</span>
                . Current paid members:
                <span class="font-medium">{{ paidSeats }}</span>
            </p>
            <p v-if="!canManageMembers" class="text-sm text-amber-600">
                Only admins can manage members.
            </p>
        </div>

        <div
            class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between"
        >
            <div class="relative w-full md:max-w-md">
                <Search
                    class="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                />
                <Input
                    v-model="searchQuery"
                    class="pl-9"
                    placeholder="Search members (name or email)"
                />
            </div>
            <div class="flex items-center rounded-md border p-1 w-fit">
                <Button
                    size="sm"
                    variant="ghost"
                    :class="membersView === 'members' ? 'bg-primary10' : ''"
                    @click="membersView = 'members'"
                >
                    Members
                </Button>
                <Button
                    size="sm"
                    variant="ghost"
                    :class="membersView === 'invitations' ? 'bg-primary10' : ''"
                    @click="membersView = 'invitations'"
                >
                    Invitations
                </Button>
            </div>
        </div>

        <Separator />

        <Table v-if="membersView === 'members'">
            <TableHeader>
                <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead class="w-[160px]">Date added</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                <TableRow
                    v-for="member in filteredMembers"
                    :key="member.id"
                >
                    <TableCell>
                        <div class="flex items-center gap-3">
                            <div
                                class="flex h-8 w-8 items-center justify-center rounded-full bg-primary20 text-xs font-medium"
                            >
                                {{ getInitials(member.name) }}
                            </div>
                            <span class="font-medium">
                                {{ member.name }}
                                <span
                                    v-if="member.email === user.email"
                                    class="text-muted-foreground font-normal"
                                >
                                    (you)
                                </span>
                            </span>
                        </div>
                    </TableCell>
                    <TableCell class="text-muted-foreground">
                        {{ member.email }}
                    </TableCell>
                    <TableCell>
                        <template v-if="canManageMembers">
                            <Select
                                :model-value="member.role"
                                @update:model-value="member.role = $event as MemberRole"
                            >
                                <SelectTrigger class="w-32">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="admin">Admin</SelectItem>
                                    <SelectItem value="member">Member</SelectItem>
                                </SelectContent>
                            </Select>
                        </template>
                        <span
                            v-else
                            class="inline-flex rounded-md bg-primary10 px-2 py-1 text-xs font-medium capitalize"
                        >
                            {{ roleLabel(member.role) }}
                        </span>
                    </TableCell>
                    <TableCell class="text-muted-foreground">
                        {{ member.joinedAt }}
                    </TableCell>
                </TableRow>
                <TableRow v-if="filteredMembers.length === 0">
                    <TableCell
                        colspan="4"
                        class="text-center text-muted-foreground"
                    >
                        No results.
                    </TableCell>
                </TableRow>
            </TableBody>
        </Table>

        <Table v-else>
            <TableHeader>
                <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead class="w-[160px]">Invited at</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                <TableRow
                    v-for="invite in filteredInvitations"
                    :key="invite.id"
                >
                    <TableCell>
                        <div class="flex items-center gap-3">
                            <div
                                class="flex h-8 w-8 items-center justify-center rounded-full bg-primary20 text-xs font-medium"
                            >
                                {{ getInitials(invite.email) }}
                            </div>
                            <span class="font-medium">Pending invite</span>
                        </div>
                    </TableCell>
                    <TableCell class="text-muted-foreground">
                        {{ invite.email }}
                    </TableCell>
                    <TableCell>
                        <span
                            class="inline-flex rounded-md bg-primary10 px-2 py-1 text-xs font-medium capitalize"
                        >
                            {{ roleLabel(invite.role) }}
                        </span>
                    </TableCell>
                    <TableCell class="text-muted-foreground">
                        {{ invite.invitedAt }}
                    </TableCell>
                </TableRow>
                <TableRow v-if="filteredInvitations.length === 0">
                    <TableCell
                        colspan="4"
                        class="text-center text-muted-foreground"
                    >
                        No results.
                    </TableCell>
                </TableRow>
            </TableBody>
        </Table>

        <Dialog v-model:open="isInviteDialogOpen">
            <DialogContent class="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>Add member</DialogTitle>
                    <DialogDescription>
                        Invite a teammate by email. Billing applies at
                        27€/member/month.
                    </DialogDescription>
                </DialogHeader>

                <div class="space-y-4">
                    <div class="space-y-2">
                        <Label for="invite-email"> Member email </Label>
                        <Input
                            id="invite-email"
                            v-model="newMemberEmail"
                            placeholder="name@company.com"
                            type="email"
                        />
                    </div>

                    <div class="space-y-2">
                        <Label for="invite-role">Role</Label>
                        <Select v-model="newMemberRole" name="invite-role">
                            <SelectTrigger id="invite-role">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="member">Member</SelectItem>
                                <SelectItem value="admin">Admin</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <p v-if="inviteError" class="text-sm text-destructive">
                        {{ inviteError }}
                    </p>
                </div>

                <DialogFooter class="gap-2">
                    <DialogClose as-child>
                        <Button variant="ghost">Cancel</Button>
                    </DialogClose>
                    <Button @click="addMemberInvitation">
                        Send invitation
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    </div>
</template>
